import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import crossIcon from '@/assets/icons/crossIcon.svg';
import historyButton from '@/assets/icons/historyButton.svg';
import leftwardIcon from '@/assets/icons/leftwardIcon.svg';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { HighlightText } from '@/components/ui/highlight-text';
import { SearchInput } from '@/components/ui/search-input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDeleteSession,
  useSessionsHistory,
} from '@/features/assistant/hooks/useSessionsHistory';
import { useDebounce } from '@/utils/hooks/useDebounce';
import { useIntersectionObserver } from '@/utils/hooks/useIntersectionObserver';
import { ROUTES, getAssistantSessionRoute } from '@/utils/routeUtils';
import { useOfficeContextStore } from '@/utils/stores/useOfficeContextStore';
import { formatTimestamp } from '@/utils/utils';

export function HistoryPage() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isCurrentSessionDeleted, setIsCurrentSessionDeleted] = useState(false);

  const currentSessionId = useOfficeContextStore((s) => s.currentSessionId);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const trimmedSearch = debouncedSearch.trim() || undefined;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useSessionsHistory({
    limit: 10,
    ...(trimmedSearch !== undefined && { search: trimmedSearch }),
  });

  const deleteSessionMutation = useDeleteSession();

  const historyItems = useMemo(() => data?.pages.flatMap((page) => page.sessions) ?? [], [data]);

  const { ref: observerTargetRef } = useIntersectionObserver(
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    {
      enabled: hasNextPage && !isFetchingNextPage,
      root: scrollContainerRef,
      rootMargin: '100px',
      threshold: 0.1,
    },
  );

  const handleBack = () => {
    if (isCurrentSessionDeleted) {
      navigate(ROUTES.ASSISTANT, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const handleHistoryItemClick = (sessionId: string) => {
    navigate(getAssistantSessionRoute(sessionId), {
      replace: true,
      state: { forceSessionHistoryRefetch: true },
    });
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>, sessionId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSessionToDelete(sessionId);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) {
      return;
    }

    try {
      await deleteSessionMutation.mutateAsync(sessionToDelete);
      setIsCurrentSessionDeleted(sessionToDelete === currentSessionId);
      navigate(ROUTES.ASSISTANT, { replace: true });
      toast.success(t('assistant.history.deleteSuccess'));
      setSessionToDelete(null);
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error(t('assistant.history.deleteFailed'), {
        description: error instanceof Error ? error.message : t('assistant.chat.unexpectedError'),
      });
    }
  };

  return (
    <div className="bg-back-bg2 flex h-full flex-1 flex-col overflow-hidden">
      <div className="max-w-page mx-auto flex h-full w-full flex-col overflow-hidden">
        {/* Header */}
        <div className="flex h-11 w-full items-center pt-2 pr-3 pb-2 pl-3">
          <button
            aria-label={t('assistant.history.backAria')}
            className="mr-2 flex cursor-pointer items-center justify-center hover:opacity-70"
            onClick={handleBack}
          >
            <img
              alt={t('assistant.history.backAlt')}
              className="icon-themed h-4 w-4"
              src={leftwardIcon}
            />
          </button>
          <h1 className="text-secondary font-['Inter'] text-sm leading-[160%] font-medium tracking-normal">
            {t('assistant.history.title')}
          </h1>
        </div>

        {/* Search Bar */}
        <div className="px-3 pb-3">
          <SearchInput
            onChange={setSearchQuery}
            placeholder={t('assistant.history.searchPlaceholder')}
            value={searchQuery}
          />
        </div>

        {/* History List */}
        <div className="custom-scrollbar flex-1 overflow-y-auto px-3 pb-3" ref={scrollContainerRef}>
          {isLoading ? (
            <HistoryListSkeleton count={5} />
          ) : (
            <div className="flex flex-col">
              {historyItems.length > 0 ? (
                <>
                  {historyItems.map((item, index) => (
                    <div key={item.id}>
                      {index > 0 && (
                        <div className="px-3">
                          <div className="border-border-subtle hover:border-card mx-3 border-b" />
                        </div>
                      )}
                      <div
                        className={`hover:bg-card flex cursor-pointer items-start gap-2.5 rounded-sm px-3 py-3.5 transition-colors ${currentSessionId === item.id ? 'bg-info-bg' : ''}`}
                        onClick={() => handleHistoryItemClick(item.id)}
                      >
                        <div className="shrink-0">
                          <img
                            alt={t('assistant.history.itemAlt')}
                            className="icon-themed h-6 w-6 rounded-md"
                            src={historyButton}
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <h3 className="text-secondary mb-1 font-['Inter'] text-[13px] leading-[170%] font-medium tracking-normal">
                            <HighlightText searchQuery={searchQuery} text={item.title ?? '-'} />
                          </h3>
                          <p className="text-neutral-mid font-['Inter'] text-[11px] leading-[160%] font-medium tracking-normal">
                            {formatTimestamp(item.createdAt, i18n.language)}
                          </p>
                        </div>

                        <Button
                          aria-label="Delete session"
                          className="-mt-1 h-auto w-auto shrink-0 self-start p-0 hover:opacity-70 disabled:opacity-40"
                          disabled={deleteSessionMutation.isPending && sessionToDelete === item.id}
                          onClick={(e) => handleDeleteClick(e, item.id)}
                          size="icon"
                          variant="ghost"
                        >
                          <img alt="Delete" className="icon-themed h-8 w-8" src={crossIcon} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="h-1" ref={observerTargetRef} />
                  {isFetchingNextPage && (
                    <div className="py-4">
                      <HistoryListSkeleton count={3} />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-neutral-mid py-8 text-center">
                  <p className="font-['Inter']">{t('assistant.history.empty')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmationDialog
        cancelLabel={t('assistant.delete.cancel')}
        confirmLabel={t('assistant.delete.confirm')}
        description={t('assistant.history.deleteConfirmDescription')}
        isPending={deleteSessionMutation.isPending}
        onConfirm={handleConfirmDelete}
        onOpenChange={(open) => {
          if (!open) {
            setSessionToDelete(null);
          }
        }}
        open={sessionToDelete !== null}
        title={t('assistant.history.deleteConfirmTitle')}
      />
    </div>
  );
}

function HistoryListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="flex flex-col">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index}>
          {index > 0 && (
            <div className="px-3">
              <div className="border-border-subtle mx-3 border-b" />
            </div>
          )}
          <div className="flex items-start gap-2.5 px-3 py-3.5">
            <div className="shrink-0">
              <Skeleton className="h-6 w-6 rounded-md" />
            </div>
            <div className="min-w-0 flex-1">
              <Skeleton className="mb-1 h-3.25 w-3/4" />
              <Skeleton className="h-2.75 w-1/2" />
            </div>
            <div className="-mt-1 shrink-0 self-start">
              <Skeleton className="h-8 w-8 rounded-sm" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
